import React, { useEffect, useState } from 'react';
import { Button, Upload, Modal, List, Row, Col } from 'antd';
import { Loading } from 'Components';
import { UploadOutlined, FileOutlined, SyncOutlined } from '@ant-design/icons';
import { FileText, X } from 'react-feather';
import './index.scss';
import moment from 'moment';

const renderInnerContent = (Icon, text) => (
  <>
    <p className="ant-upload-drag-icon">
      <Icon />
    </p>
    <p className="ant-upload-text">{text}</p>
  </>
);

const UploadDragger = ({ handleUpload }) => (
  <Upload.Dragger
    customRequest={handleUpload}
    showUploadList={false}
    maxCount={1}
  >
    { renderInnerContent(UploadOutlined, "Click or drag file to this area to upload") }
  </Upload.Dragger>
);

const FileSelector = ({ visible, setVisible, existingFiles, handleSetValue, setUploadedFile }) => (
  <div>
    <div
      className="ant-upload ant-upload-drag"
      onClick={() => setVisible(true)}
    >
      <div
        className="ant-upload-drag-container"
        style={{
          padding: "16px 0",
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {renderInnerContent(FileOutlined, "Select from previously uploaded file")}
      </div>
    </div>
    <Modal
      title="Select a file"
      centered
      visible={visible}
      bodyStyle={{ height: "70vh" }}
      width={800}
      onOk={() => setVisible(false)}
      onCancel={() => setVisible(false)}
    >
      <List
        size="small"
        className="weaver-file-list"
        dataSource={existingFiles}
        renderItem={(file, i) => (
          <List.Item key={`${file.id()}-${i}`}>
            <a
              onClick={() => {
                handleSetValue(file);
                setUploadedFile(file.fileName);
                setVisible(false);
              }}
            >
              {file.fileName}
            </a>
          </List.Item>
        )}
      />
    </Modal>
  </div>
);

const UploadingFile = () => (
  <div
    style={{
      display: 'flex',
      width: '100%',
      padding: '20px',
      borderRadius: '9px',
      margin: '5px',
      border: '1px solid var(--borderColor)',
      alignItems: 'center',
      flexDirection: 'column',
    }}
  >
    <SyncOutlined spin />
    <p style={{ marginBottom: '0px' }}>Uploading file...</p>
  </div>
);

const UploadedFileUI = ({ uploadedFile, form, field, setUploadedFile }) => (
  <>
    <Col flex="auto" className="uploaded-file">
      <FileText size="48" strokeWidth="0.5px" />
      <p>{uploadedFile}</p>
    </Col>
    <Col flex="10px">
      <X
        onClick={() => {
          form.setFieldsValue({ [field]: undefined });
          setUploadedFile(null);
        }}
      />
    </Col>
  </>
);

const CompactUploader = ({ handleUpload, setVisible, visible, existingFiles, handleSetValue, setUploadedFile, uploading, disabled }) => (
  <>
    <Col style={{ flex: 3 }}>
      <Upload style={{ width: '100%' }}>
        <Button
          style={{ width: '100%' }}
          onClick={handleUpload}
          icon={<UploadOutlined />}
          disabled={uploading || disabled}
        >
          {uploading ? 'Uploading file...' : 'Click to upload file'}
        </Button>
      </Upload>
    </Col>
    <Col style={{ flex: 3 }}>
      <Button
        style={{ width: '100%' }}
        onClick={() => setVisible(true)}
        disabled={uploading || disabled}
      >
        Select existing file
      </Button>
      <FileSelector
        visible={visible}
        setVisible={setVisible}
        existingFiles={existingFiles}
        handleSetValue={handleSetValue}
        setUploadedFile={setUploadedFile}
      />
    </Col>
  </>
);

const CompactUploaded = ({ uploadedFile, form, field, setUploadedFile }) => (
  <>
    <Col flex="auto" className="uploaded-file">
      <p>{uploadedFile}</p>
    </Col>
    <Col flex="10px">
      <X
        onClick={() => {
          form.setFieldsValue({ [field]: undefined });
          setUploadedFile(null);
        }}
      />
    </Col>
  </>
);

const UploadFileInput = ({ form, field, isCompact, disabled, onFileSelect, ...props }) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [visible, setVisible] = useState(false);
  const [existingFiles, setExistingFiles] = useState([]);
  const [fetchingFiles, setFetchingFiles] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const files = await Weaver.File.list();
        setExistingFiles(files);
      } finally {
        setFetchingFiles(false);
      }
    };
    fetchFiles();
  }, []);

  useEffect(() => {
    const fileId = form?.getFieldValue(field);
    if (fileId && existingFiles.length > 0) {
      const weaverFile = existingFiles.find(f => f.id() === fileId);
      setUploadedFile(weaverFile?.fileName || null);
    }
  }, [existingFiles, form, field]);

  const handleSetValue = file => {
    form.setFieldsValue({ [field]: file.id() });
    onFileSelect({ [field]: file.id() })
  };

  const handleUpload = async ({ file, onSuccess }) => {
    if (!file) return;
    setUploading(true);

    try {
      file.uploadTime = moment();
      const weaverFile = new Weaver.File(file);

      form.setFieldsValue({
        [field]: weaverFile,
      });

      setUploadedFile(weaverFile.fileName);
      onSuccess();
    } catch (error) {
      console.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const renderCompactInput = () => (
    <Row gutter={8}>
      {uploadedFile ? (
        <CompactUploaded
          uploadedFile={uploadedFile}
          form={form}
          field={field}
          setUploadedFile={setUploadedFile}
        />
      ) : (
        <CompactUploader
          handleUpload={handleUpload}
          setVisible={setVisible}
          visible={visible}
          existingFiles={existingFiles}
          handleSetValue={handleSetValue}
          setUploadedFile={setUploadedFile}
          uploading={uploading}
          disabled={disabled}
        />
      )}
    </Row>
  );

  const renderExtendedInput = () => (
    <Row gutter={16}>
      {uploading ? (
        <UploadingFile />
      ) : uploadedFile ? (
        <UploadedFileUI
          uploadedFile={uploadedFile}
          form={form}
          field={field}
          setUploadedFile={setUploadedFile}
        />
      ) : (
        <>
          <Col flex="auto" className="choose-file">
            <UploadDragger handleUpload={handleUpload} />
          </Col>
          <Col flex="auto" className="choose-file">
            <FileSelector
              visible={visible}
              setVisible={setVisible}
              existingFiles={existingFiles}
              handleSetValue={handleSetValue}
              setUploadedFile={setUploadedFile}
            />
          </Col>
        </>
      )}
    </Row>
  );

  const renderUi = () => (isCompact ? renderCompactInput() : renderExtendedInput());

  return (
    <div>
      {fetchingFiles ? (
        <Loading
          style={{
            display: 'flex',
            textAlign: 'center',
            width: '100%',
            height: '200px',
            alignItems: 'center',
            background: 'var(--background)',
          }}
        />
      ) : (
        renderUi()
      )}
    </div>
  );
};

export default UploadFileInput;
